import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api from "@/lib/api";
import {
  UserPlus, Pencil, Trash2, LogIn, PenLine, FileText, Shield, Search,
} from "lucide-react";

const ACTION_META = {
  create: { label: "Creó", icon: UserPlus, color: "#34C759" },
  update: { label: "Modificó", icon: Pencil, color: "#FF9500" },
  delete: { label: "Eliminó", icon: Trash2, color: "#FF3B30" },
  login: { label: "Inició sesión", icon: LogIn, color: "#0066CC" },
  register: { label: "Registró empresa", icon: Shield, color: "#5856D6" },
  sign: { label: "Firmó", icon: PenLine, color: "#34C759" },
};

const ENTITY_LABEL = {
  client: "cliente", technician: "técnico", aviso: "aviso",
  work_order: "orden de trabajo", auth: "",
};

export default function Audit() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/audit").then((r) => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const filtered = logs.filter((l) =>
    (l.user_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (l.entity || "").toLowerCase().includes(q.toLowerCase()) ||
    (l.action || "").toLowerCase().includes(q.toLowerCase())
  );

  const fmt = (ts) => {
    try { return new Date(ts).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return ts; }
  };

  return (
    <div className="p-8 lg:p-12">
      <PageHeader title="Auditoría" subtitle="Registro de toda la actividad de tu empresa · quién, qué, cuándo y desde dónde" />

      <div className="mb-6 flex items-center gap-2 rounded-2xl border border-[#D2D2D7]/50 bg-white px-4 py-3">
        <Search className="h-4 w-4 text-[#86868B]" />
        <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por usuario, acción o entidad..." className="flex-1 bg-transparent text-sm outline-none" />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-white/60" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Sin actividad registrada" subtitle="Cada creación, modificación, borrado, inicio de sesión y firma quedará registrado aquí." />
      ) : (
        <div className="overflow-hidden rounded-3xl border border-[#D2D2D7]/50 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#D2D2D7]/50 text-xs uppercase tracking-wider text-[#86868B]">
                <th className="px-6 py-4 font-semibold">Acción</th>
                <th className="px-6 py-4 font-semibold">Usuario</th>
                <th className="px-6 py-4 font-semibold">Detalle</th>
                <th className="px-6 py-4 font-semibold">IP</th>
                <th className="px-6 py-4 font-semibold">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const meta = ACTION_META[l.action] || { label: l.action, icon: FileText, color: "#86868B" };
                const Icon = meta.icon;
                return (
                  <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-[#D2D2D7]/30 transition-colors hover:bg-[#F5F5F7]/50">
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium" style={{ color: meta.color, backgroundColor: `${meta.color}15` }}>
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                        {meta.label}{ENTITY_LABEL[l.entity] !== undefined ? (ENTITY_LABEL[l.entity] ? " " + ENTITY_LABEL[l.entity] : "") : " " + l.entity}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-[#1D1D1F]">{l.user_name || "—"}</td>
                    <td className="px-6 py-4 text-[#86868B]">{l.details || "—"}</td>
                    <td className="px-6 py-4 font-mono text-xs text-[#86868B]">{l.ip}</td>
                    <td className="px-6 py-4 text-[#86868B]">{fmt(l.timestamp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
